import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { Document, ChevronDown, ChevronUp } from '@/assets/icons'
import { selectMedSummaryAssetFiles } from '../../store/selectors'
import { MedSummaryAssetActions } from '../../store/slice'

/**
 * Collapsed-by-default reference list of the files already in the billing summary.
 * Count comes from the asset overview's total_files; the roster is lazy-loaded on
 * first expand.
 */
export function IncludedInSummaryDrawer({ assetId, totalFiles }: { assetId: string; totalFiles: number }) {
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const files = useAppSelector((state: RootState) => selectMedSummaryAssetFiles(state, { assetId }))

  if (totalFiles <= 0) return null

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && !files) dispatch(MedSummaryAssetActions.refreshAssetData({ assetId }))
  }

  const Chevron = open ? ChevronUp : ChevronDown
  return (
    <div className="border-y border-brand-neutral-200">
      <button type="button" onClick={toggle} className="w-full flex items-center justify-between py-1.5 min-h-[48px]" aria-expanded={open}>
        <div className="flex items-center gap-2.5">
          <Document className="w-6 h-6 text-brand-neutral-800" />
          <span className="text-base font-semibold text-brand-neutral-950">Included in billing summary</span>
          <span className="inline-flex items-center justify-center min-w-[18px] h-[22px] px-1.5 rounded-full bg-brand-neutral-200 text-sm font-semibold text-brand-neutral-700">
            {totalFiles}
          </span>
        </div>
        <span className="flex items-center justify-center p-0.5 rounded-full border border-brand-neutral-300 bg-brand-neutral-200">
          <Chevron className="w-4 h-4 text-brand-neutral-700" />
        </span>
      </button>
      {open && (
        <div className="pb-3 flex flex-col gap-1.5">
          {files === null ? (
            <p className="text-xs text-brand-neutral-500 pl-[34px]">Loading…</p>
          ) : files.length === 0 ? (
            <p className="text-xs text-brand-neutral-500 pl-[34px]">No files yet.</p>
          ) : (
            files.map((file, idx) => (
              <p key={`${idx}-${file.display_name}`} className="text-[13px] text-brand-neutral-800 truncate pl-[34px]">
                {file.display_name}
              </p>
            ))
          )}
        </div>
      )}
    </div>
  )
}
